import React, { useMemo, useState } from 'react'

import {
  getPrincipalsManagerFullNames,
  getPrincipalsManagerHalfNames,
  getPrincipalsOrganizationNamesWithInnAndKpp,
  getRepresentativesFullNames,
  getRepresentativesInns,
  useGetMRFsByIds,
  User,
} from '@builddocs/data-management'
import { Button } from '@builddocs/mui'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Typography, Box, Skeleton, Stack } from '@mui/material'
import { format, parseISO } from 'date-fns'
import { ru } from 'date-fns/locale'

const typographyStyle = {
  fontSize: '14px',
  fontWeight: '400',
  lineHeight: '20px',
  color: '#888888',
}

const getCorrectDate = (date: string, isCheck?: boolean) => {
  if (isCheck) {
    return format(parseISO(date), 'dd.MM.yyyy HH:mm', {
      locale: ru,
    })
  }
  return format(parseISO(date), 'dd.MM.yyyy', {
    locale: ru,
  })
}

type SignerWithPoa = {
  fio: string
  poaId: string
  signingDateTime?: string
}

export const SingingPoaInfo = ({
  user: _user,
  title = 'Информация о доверенностях',
  signersWithPoa = [],
}: {
  user: User
  title?: string
  signersWithPoa?: SignerWithPoa[]
}): React.ReactElement => {
  const [expandedByKey, setExpandedByKey] = useState<Record<string, boolean>>(
    {},
  )
  const [expandedPermissionsByKey, setExpandedPermissionsByKey] = useState<
    Record<string, boolean>
  >({})
  // Формируем список идентификаторов доверенностей из подписантов
  const relevantPoaIds = signersWithPoa.map(s => s.poaId)

  // Получаем доверенности по списку идентификаторов
  const { data, isLoading } = useGetMRFsByIds({
    ids: relevantPoaIds,
    enabled: true,
  })

  // Мапа для быстрого получения ФИО из входного списка по poaId
  const poaIdToFio = useMemo(() => {
    const map: Record<string, string> = {}

    for (const { poaId, fio } of signersWithPoa) {
      if (poaId) map[poaId] = fio
    }
    return map
  }, [signersWithPoa])

  // Мапа для получения даты подписания по poaId
  const poaIdToSigningDateTime = useMemo(() => {
    const map: Record<string, string | undefined> = {}

    for (const { poaId, signingDateTime } of signersWithPoa) {
      if (poaId) map[poaId] = signingDateTime
    }
    return map
  }, [signersWithPoa])
  const apiItems =
    data?.map((poa, index) => ({
      key: String(poa?.poaIdInternal || poa?.poaNumber || index),
      headerName:
        (poa?.poaIdInternal && poaIdToFio[poa.poaIdInternal]) ||
        getPrincipalsManagerHalfNames(poa),
      poaIdInternal: poa?.poaIdInternal,
      statusUpdatedAt: poa?.statusUpdatedAt,
      statusLabel:
        poa?.status === 'valid'
          ? 'Валидный'
          : poa?.status === 'invalid'
          ? 'Не валидный'
          : 'Ожидает проверки',
      principalOrg: getPrincipalsOrganizationNamesWithInnAndKpp(poa),
      principalsManagerFullNames: getPrincipalsManagerFullNames(poa),
      issued: poa?.issued,
      validUntil: poa?.validUntil,
      poaNumber: poa?.poaNumber,
      representativesFullNames: getRepresentativesFullNames(poa),
      representativesInns: getRepresentativesInns(poa),
      signingDateTime:
        poa?.poaIdInternal && poaIdToSigningDateTime[poa.poaIdInternal],
      permissionsDictionary:
        poa?.permissions?.dictionary?.map(
          (item: { code: string; name: string }) => item?.name,
        ) ?? [],
      permissionsText: poa?.permissions?.text,
    })) ?? []

  const itemsToRender = apiItems.length ? apiItems : []

  return (
    <Box sx={{ mt: 2, minHeight: '100%' }}>
      <Typography
        sx={{
          fontSize: '17.5px',
          fontWeight: '500',
          lineHeight: '32px',
        }}
      >
        {signersWithPoa?.length > 0
          ? title
          : 'Доверенности не были использованы'}
      </Typography>
      {isLoading && <Skeleton />}
      <Box>
        {itemsToRender.map((item, index) => (
          <Box
            key={item.key}
            sx={{
              borderBottom:
                index !== itemsToRender.length - 1 ? '1px solid' : 'none',
              borderColor: 'divider',
              py: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                py: 1,
              }}
              onClick={() =>
                setExpandedByKey(prev => ({
                  ...prev,
                  [item.key]: !prev[item.key],
                }))
              }
            >
              <Stack>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: '400',
                    lineHeight: '20px',
                    color: '#0034AD',
                  }}
                >
                  {item.headerName}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: '400',
                    lineHeight: '20px',
                    marginTop: '4px',
                  }}
                >
                  {item.poaIdInternal}
                </Typography>
                <Typography sx={typographyStyle}>
                  Доверенность прошла проверку{' '}
                  {item.statusUpdatedAt
                    ? getCorrectDate(item.statusUpdatedAt, true)
                    : '-'}
                </Typography>
              </Stack>
              <ExpandMoreIcon
                sx={{
                  transform: expandedByKey[item.key]
                    ? 'rotate(180deg)'
                    : 'none',
                  transition: 'transform 0.2s',
                }}
              />
            </Box>
            {expandedByKey[item.key] && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  pt: 1,
                }}
              >
                <Box>
                  <Typography sx={typographyStyle}>ИНН</Typography>
                  <Typography>{item.representativesInns || '-'}</Typography>
                </Box>

                <Box>
                  <Typography sx={typographyStyle}>Доверитель</Typography>
                  <Typography>{item.principalOrg}</Typography>
                </Box>

                <Box>
                  <Typography sx={typographyStyle}>ФИО доверителя</Typography>
                  <Typography>{item.principalsManagerFullNames}</Typography>
                </Box>

                <Box>
                  <Typography sx={typographyStyle}>Дата подписания</Typography>
                  <Typography>
                    {item.signingDateTime
                      ? getCorrectDate(item.signingDateTime, true)
                      : '-'}
                  </Typography>
                </Box>

                <Box>
                  <Button
                    sx={{
                      fontSize: '14px',
                      fontWeight: '400',
                      lineHeight: '20px',
                      color: '#0034AD',
                      padding: '0px',
                      margin: '0px',
                      border: 'none',
                      textTransform: 'none',
                      textAlign: 'left',
                      justifyContent: 'flex-start',
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                    variant="text"
                    onClick={() =>
                      setExpandedPermissionsByKey(prev => ({
                        ...prev,
                        [item.key]: !prev[item.key],
                      }))
                    }
                  >
                    Полномочия{' '}
                    <ExpandMoreIcon
                      sx={{
                        transform: expandedPermissionsByKey[item.key]
                          ? 'rotate(180deg)'
                          : 'none',
                        transition: 'transform 0.15s ease-in-out',
                      }}
                    />
                  </Button>
                  {expandedPermissionsByKey[item.key] && (
                    <Stack spacing="10px">
                      <Box>
                        <Typography sx={typographyStyle}>
                          Из классификатора
                        </Typography>
                        <Typography>
                          {item.permissionsDictionary?.length
                            ? item.permissionsDictionary.join(', ')
                            : '-'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={typographyStyle}>
                          Текстовые полномочия
                        </Typography>
                        <Typography>{item.permissionsText}</Typography>
                      </Box>
                    </Stack>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  )
}
